// The andon slice: the raise draft, where the raise is up to, how an alert was
// asked to open, whether the live stream is up, and the two things the server
// says about andons at sign-in (GIFs on or off, the acknowledge targets).
//
// Folded in from the Andon app's `store/slices/ui.ts` and `session.ts`. What
// is NOT here is anything the shop floor already holds: the operator, the
// station, the language, the speech consent and the clock skew all live in the
// session and settings slices and are read from there. A second copy of the
// station is how a raise ends up filed against the wrong work centre.
//
// Nothing in this slice is persisted, and that matches the Andon app, which
// persisted none of it either. A raise draft from an hour ago riding along
// with a raise somebody starts now is exactly the failure the Andon app's
// `startRaise` reset exists to prevent, and a reload is a fine place to lose a
// draft: the station and the pod are one tap each to put back.
//
// `selectedAlertId` did not come across. The shop floor's `openAndonId` on the
// UI slice is the same fact — which andon is open — and the detail sheet
// already reads it; a second id for the same thing would have to be kept in
// step with the first.

import type { StateCreator } from 'zustand';
import type { AlertTypeKey, AndonRules, Stars } from '@/api/andon/types';

/** One thing the operator has attached to a raise that has not gone out yet. */
export interface DraftAttachment {
  /** Stable within the draft, so a row can be removed by identity. */
  id: string;
  kind: 'photo' | 'audio' | 'video' | 'file';
  name: string;
  /** base64, with the data: prefix stripped. Uploaded once the alert exists. */
  data: string;
  /** Bytes, for the row's subtitle. */
  size: number;
  /** Seconds, for a voice or video clip. Null for anything else. */
  seconds: number | null;
}

export interface AndonSlice {
  /**
   * Open the alert straight onto the close card.
   *
   * Set by whoever opens an alert with a verb already in mind — the board's
   * drop onto CLOSED, in the Andon app — because the close needs the detail
   * (the measured stop, the pods, the cause taxonomy) which the opener does
   * not hold. Read once by the alert screen and cleared, so reopening the
   * same alert later lands on the thread as usual.
   */
  alertIntent: 'close' | null;
  /** Severity picked on step one, held until the send. */
  draftStars: Stars;
  /** Pod chosen by scan or search, prefilled into the next raise. */
  draftPodId: number | null;
  draftPodName: string | null;
  /** Words typed or dictated on the raise card. */
  draftWords: string | null;
  /** The tile that was tapped. */
  draftType: AlertTypeKey | null;
  /**
   * The answers, in the order they were put.
   *
   * They live in the draft rather than on the alert because there is no alert
   * yet — that is the point of them. They travel with the raise call as the
   * asked register, and are cleared with everything else the moment it
   * succeeds.
   */
  draftAnswers: Array<{ question: string; answer: string }>;
  /** Photos, clips and files stacked against the raise before it goes out. */
  draftAttachments: DraftAttachment[];
  /**
   * Whether the BFF's live stream is connected.
   *
   * Screens poll slower while it is, and at their normal rate while it is not,
   * so a dropped stream degrades to exactly the behaviour that existed before
   * there was one.
   */
  live: boolean;
  /** The server has a Giphy key and offers GIFs on the thread. */
  andonGifs: boolean;
  /** The acknowledge targets per severity. Null until the server has said. */
  andonRules: AndonRules | null;
  /**
   * A counter, bumped whenever this device's person closes an andon.
   *
   * A COUNTER AND NOT A FLAG, because the interesting thing is the moment
   * rather than the state: there is nothing to be "currently celebrating",
   * and a boolean would need whoever set it to remember to unset it. The
   * shell watches this for changes and throws the paper — see
   * `components/andon/Confetti`.
   */
  celebrate: number;

  /** Open an alert with a verb in mind; null is the ordinary open. */
  setAlertIntent: (intent: 'close' | null) => void;
  /** The alert screen has read `alertIntent`; it is done with. */
  clearAlertIntent: () => void;
  setDraftStars: (s: Stars) => void;
  setDraftPod: (id: number | null, name: string | null) => void;
  setDraftWords: (w: string | null) => void;
  setDraftType: (t: AlertTypeKey | null) => void;
  addDraftAnswer: (question: string, answer: string) => void;
  /**
   * Forget answers to questions that are no longer being asked.
   *
   * For one case: an operator changes a constant that DECIDES the questions
   * under it — "the whole trolley" back to "one part" — and the trolley they
   * had already named is now an answer to a question this raise no longer
   * puts. Left in the draft it would ride to Odoo as a fact about an andon it
   * describes nothing on.
   */
  dropDraftAnswers: (questions: string[]) => void;
  addDraftAttachment: (a: DraftAttachment) => void;
  removeDraftAttachment: (id: string) => void;
  /** Clear the whole raise draft — called after every successful raise. */
  resetDraft: () => void;
  /**
   * Begin a raise on a clean draft.
   *
   * A reset with a name, so a draft from an hour ago can never ride along
   * with a raise somebody starts now. Whether the raise sheet is showing is
   * the UI slice's business; this only readies the draft. The raise is one
   * sheet (frame v2.12), so there is no step to put back to the start.
   */
  startRaise: () => void;
  setLive: (v: boolean) => void;
  setAndonFeatures: (gifs: boolean, rules: AndonRules | null) => void;
  /** The fix landed. The one moment on the close sheet worth marking. */
  cheer: () => void;
}

/** The draft, as it is at rest. One definition, so reset and boot agree. */
const EMPTY_DRAFT = {
  draftPodId: null,
  draftPodName: null,
  draftWords: null,
  draftType: null,
  draftAnswers: [],
  draftAttachments: [],
  draftStars: 3 as Stars,
};

export const createAndonSlice: StateCreator<AndonSlice, [], [], AndonSlice> = (set) => ({
  alertIntent: null,
  ...EMPTY_DRAFT,
  live: false,
  andonGifs: false,
  andonRules: null,
  celebrate: 0,

  setAlertIntent: (alertIntent) => set({ alertIntent }),
  clearAlertIntent: () => set({ alertIntent: null }),
  setDraftStars: (draftStars) => set({ draftStars }),
  setDraftPod: (draftPodId, draftPodName) => set({ draftPodId, draftPodName }),
  setDraftWords: (draftWords) => set({ draftWords }),
  setDraftType: (draftType) => set({ draftType }),
  // Last answer wins, so going back and re-tapping a chip corrects the answer
  // rather than sending both.
  addDraftAnswer: (question, answer) =>
    set((s) => ({ draftAnswers: [...s.draftAnswers.filter((a) => a.question !== question), { question, answer }] })),
  dropDraftAnswers: (questions) =>
    set((s) => ({ draftAnswers: s.draftAnswers.filter((a) => !questions.includes(a.question)) })),
  addDraftAttachment: (a) => set((s) => ({ draftAttachments: [...s.draftAttachments, a] })),
  removeDraftAttachment: (id) => set((s) => ({ draftAttachments: s.draftAttachments.filter((a) => a.id !== id) })),
  resetDraft: () => set({ ...EMPTY_DRAFT }),
  startRaise: () => set({ alertIntent: null, ...EMPTY_DRAFT }),
  setLive: (live) => set({ live }),
  setAndonFeatures: (andonGifs, andonRules) => set({ andonGifs, andonRules }),
  cheer: () => set((s) => ({ celebrate: s.celebrate + 1 })),
});
